export type EmailAccountType = {
  email_address: string;
  imap_username: string;
  imap_host: string;
  imap_port: number | null;
  imap_use_ssl: boolean;
  has_password: boolean;
  // Plugged into the shared signature template's {{lead_manager_*}}
  // placeholders at send time — see SharedEmailSignature.render() server-side.
  display_name: string;
  position: string;
  phone: string;
  is_active: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
};

// The single, admin-managed HTML template appended to every outgoing email.
// {{lead_manager_name}}, {{lead_manager_position}}, {{lead_manager_email}},
// {{lead_manager_phone}}, {{unsubscribe_url}} get substituted per sender/send.
export type SharedSignatureType = {
  html: string;
};

export type EmailAttachmentType = {
  id: number;
  filename: string;
  content_type: string;
  size: number;
  is_inline: boolean;
  content_id: string | null;
  // Absolute URL already built server-side. <img>/<a> tags can't send an
  // Authorization header, so append `?token=<userToken>` before using it.
  url: string;
};

export type EmailMessageType = {
  id: number;
  lead_id: number | null;
  // Which Lead Manager's mailbox this belongs to — a lead can end up with
  // messages across more than one over time (reassignment, a reply landing
  // in a different inbox), so this isn't always "your own" account.
  account_email: string | null;
  direction: 'incoming' | 'outgoing' | string;
  folder: 'inbox' | 'sent' | string;
  subject: string;
  from_address: string;
  to_addresses: string;
  cc_addresses: string;
  body_text: string;
  body_html: string;
  is_read: boolean;
  sent_at: string | null;
  created_at: string;
  attachments: EmailAttachmentType[];
  message_id?: any;
};

export type EmailsPagination = {
  current_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_previous: boolean;
  per_page: number;
};

export type EmailsQueryParams = {
  page?: number;
  per_page?: number;
  folder?: string;
  is_read?: boolean;
  lead_id?: number;
  search?: string;
};

// Fields the email settings form submits. imap_password is only included
// when the user is actually changing it — omit it to leave it as-is.
export type EmailSettingsFormFields = {
  email_address: string;
  imap_username: string;
  imap_host: string;
  imap_port: string;
  imap_use_ssl: boolean;
  imap_password: string;
  display_name: string;
  position: string;
  phone: string;
  is_active: boolean;
};

export const emptyEmailSettingsFields: EmailSettingsFormFields = {
  email_address: '',
  imap_username: '',
  imap_host: '',
  imap_port: '',
  imap_use_ssl: true,
  imap_password: '',
  display_name: '',
  position: '',
  phone: '',
  is_active: true,
};

export type ComposeEmailFields = {
  to: string;
  cc: string;
  subject: string;
  body_text: string;
  // Set when composing from a template — the template's rendered HTML wins
  // over body_text on the backend, which only falls back to auto-wrapping
  // body_text as HTML when this is absent.
  body_html?: string;
  include_signature: boolean;
  lead_id?: number;
};
